import AddIcon from "@mui/icons-material/Add";
import { Avatar, Box, Button, Chip, TextField } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";

import { colors } from "@components/ui/colors";

import { NOTE_TYPES } from "./notesUtils";

export const ClientNoteForm = ({ inputRef, onAdd, onClose }) => {
  const [value, setValue] = useState("");
  const [type, setType] = useState("general");
  const [focused, setFocused] = useState(false);

  const currentUser = useSelector((state: any) => state.userState);

  const firstName = currentUser?.firstName?.trim() || "";

  const lastName = currentUser?.lastName?.trim() || "";

  const displayName =
    currentUser?.displayName?.trim() ||
    (currentUser?.useAliasAsDisplayName && currentUser?.alias?.trim() ? currentUser.alias.trim() : `${firstName} ${lastName}`.trim()) ||
    currentUser?.email ||
    "Me";

  const initials =
    displayName
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "ME";

  const avatar = currentUser?.avatar || currentUser?.imageUrl || null;

  const reset = () => {
    setValue("");
    setType("general");
    setFocused(false);
    inputRef?.current?.blur();

    onClose?.();
  };

  const handleSubmit = async () => {
    const content = value.trim();

    if (!content) return;

    try {
      await onAdd?.(content, type);
      reset();
    } catch (error) {
      console.error("Error adding client note", error);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 2.5,
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        transition: "all 0.2s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          p: 1.5,
        }}
      >
        <Avatar
          src={avatar || undefined}
          sx={{
            width: 30,
            height: 30,
            minWidth: 30,
            backgroundColor: colors.orange[500],
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            inputRef={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            placeholder="Write a note about this client..."
            multiline
            minRows={focused ? 3 : 1}
            maxRows={6}
            fullWidth
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: 13,
                lineHeight: 1.5,
              },

              "& textarea::placeholder": {
                color: colors.gray[400],
                opacity: 1,
              },
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }

              if (e.key === "Escape") {
                reset();
              }
            }}
          />

          {focused && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.25,
                borderTop: `1px solid ${colors.gray[200]}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  flexWrap: "wrap",
                  mb: 1.5,
                }}
              >
                {Object.entries(NOTE_TYPES).map(([key, config]) => (
                  <Chip
                    key={key}
                    label={config.label}
                    size="small"
                    onClick={() => setType(key)}
                    sx={{
                      height: 26,
                      borderRadius: 1.5,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: type === key ? config.bg : "#f9f8fb",
                      color: type === key ? config.color : colors.gray[600],
                      border: type === key ? `1px solid ${config.color}30` : "1px solid transparent",

                      "&:hover": {
                        backgroundColor: config.bg,
                      },
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button
                  size="small"
                  onClick={reset}
                  sx={{
                    textTransform: "none",
                    color: colors.gray[600],
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={!value.trim()}
                  onClick={handleSubmit}
                  sx={{
                    textTransform: "none",
                    borderRadius: 10,
                    px: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    backgroundColor: colors.orange[500],
                    boxShadow: "none",

                    "&:hover": {
                      backgroundColor: colors.orange[600],
                      boxShadow: "none",
                    },
                  }}
                >
                  Add Note
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
